import React, { forwardRef, useRef, useState } from "react";
import moment from "moment";
import Select, { components, MultiValueProps, StylesConfig } from "react-select";
import { AgingReport, AgingReportMessage } from "common";
import { useUser } from "../store";

type Option = { value: string; label: string };

const portalStyles: StylesConfig<Option, true> = {
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const DATE_FORMAT = "YYYY-MM-DD";
const QUICK_DATES: { label: string; amount: number; unit: "days" | "weeks" | "months" }[] = [
  { label: "Tomorrow", amount: 1, unit: "days" },
  { label: "3 days", amount: 3, unit: "days" },
  { label: "1 week", amount: 1, unit: "weeks" },
  { label: "1 month", amount: 1, unit: "months" },
];

// The control stays one line tall, so only the first person is shown, with a count of the rest
const RecipientValue = (props: MultiValueProps<Option, true>) => {
  if (props.index > 0) return null;
  const others = (props.selectProps.value as readonly Option[]).length - 1;
  return (
    <>
      <components.MultiValue {...props} />
      {others > 0 && <span className="message-select__more">+{others}</span>}
    </>
  );
};

/**
 * Writes into a conversation, which already has its months. Without a thread it starts a new one,
 * for the months picked here.
 */
const ReplyComposer = forwardRef<HTMLTextAreaElement, {
  report: AgingReport;
  thread?: AgingReportMessage;
  users?: any[];
  monthOptions?: Option[];
  refreshReport: (report: AgingReport) => void;
  onSent?: (months: string[]) => void;
}>(function ReplyComposer({ report, thread, users = [], monthOptions = [], refreshReport, onSent }, ref) {
  const { invokeApi } = useUser();
  const [text, setText] = useState("");
  const [deadline, setDeadline] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<readonly Option[]>([]);
  const [months, setMonths] = useState<string[] | null>(null);
  const [isSending, setIsSending] = useState(false);
  // A second click can arrive before the re-render that disables the button
  const sending = useRef(false);

  const startsNewThread = !thread;
  const selectedMonths = months ?? [];
  const shownDeadline = deadline ?? "";

  const send = async () => {
    const message = text.trim();
    if (!message || sending.current) return;
    sending.current = true;
    setIsSending(true);
    const recipientNames = recipients.length ? recipients.map((option) => option.value) : undefined;
    const target = { residentName: report.residentName, billType: report.billType, index: report.index };
    try {
      const updated: AgingReport = await invokeApi(
        startsNewThread
          ? {
              endpoint: "/putReportMessage",
              data: { ...target, message, deadline: shownDeadline, recipients: recipientNames ?? [], months: selectedMonths },
            }
          : {
              endpoint: "/addReportMessageResponse",
              data: { ...target, messageId: thread!.id, response: message, deadline: shownDeadline || undefined, recipients: recipientNames },
            }
      );
      setText("");
      setDeadline(null);
      setRecipients([]);
      setMonths(null);
      refreshReport(updated);
      if (startsNewThread) onSent?.(selectedMonths);
    } catch {
      // invokeApi already reports the failure; the text stays so it can be sent again
    } finally {
      sending.current = false;
      setIsSending(false);
    }
  };

  return (
    <form
      className="quick-reply"
      onSubmit={(event) => {
        event.preventDefault();
        send();
      }}
    >
      <textarea
        ref={ref}
        className="quick-reply-input"
        aria-label={thread ? "Reply in this conversation" : "Message"}
        placeholder={thread ? "Write a reply…" : "Write a message for this report…"}
        rows={3}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            send();
          }
        }}
        disabled={isSending}
      />
      <div className="reply-fields">
        <div className="message-field">
          <span>Follow-up date <span className="optional-label">Optional</span></span>
          <input
            type="date"
            aria-label="Follow-up date for this reply"
            value={shownDeadline}
            onChange={(event) => setDeadline(event.target.value)}
          />
          <div className="date-quick" role="group" aria-label="Quick follow-up dates">
            {QUICK_DATES.map(({ label, amount, unit }) => {
              const date = moment().add(amount, unit).format(DATE_FORMAT);
              return (
                <button key={label} type="button" className={shownDeadline === date ? "is-active" : undefined} aria-pressed={shownDeadline === date} onClick={() => setDeadline(date)}>
                  {label}
                </button>
              );
            })}
            {shownDeadline && (
              <button type="button" className="date-quick-clear" onClick={() => setDeadline("")}>
                Clear
              </button>
            )}
          </div>
        </div>
        <label className="message-field">
          <span>Notify <span className="optional-label">Optional</span></span>
          <Select
            classNamePrefix="message-select"
            aria-label="Notify recipients"
            placeholder="Choose people…"
            menuPlacement="top"
            menuPosition="fixed"
            menuPortalTarget={document.body}
            styles={portalStyles}
            isMulti
            isClearable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{ MultiValue: RecipientValue }}
            value={recipients}
            onChange={setRecipients}
            options={users.map((person: any) => ({ value: person?.username, label: person?.name || person?.username }))}
          />
        </label>
        {startsNewThread && (
          <label className="message-field">
            <span>Months <span className="optional-label">Optional</span></span>
            <Select
              classNamePrefix="message-select"
              aria-label="Related months"
              placeholder="General (no month)"
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              styles={portalStyles}
              isMulti
              options={monthOptions}
              value={monthOptions.filter((option) => selectedMonths.includes(option.value))}
              onChange={(options: readonly Option[]) => setMonths(options.map((option) => option.value))}
            />
          </label>
        )}
      </div>
      <div className="quick-reply-footer">
        <button type="submit" className="quick-reply-send" aria-label={thread ? "Send reply" : "Send message"} disabled={isSending || !text.trim()}>
          <i className="fa-solid fa-paper-plane" aria-hidden="true" />
          {isSending ? "Sending…" : startsNewThread ? "Send" : "Reply"}
        </button>
      </div>
    </form>
  );
});

export default ReplyComposer;
